import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import '../../styles/portfolio.css';
import '../../styles/glass.css';

const PortfolioSlider = () => {
  const [selectedProject, setSelectedProject] = useState(null);

  // Static list of images from public/portfolio
  const portfolioImages = [
    "1735815974153-229354721.jpg",
    "1742292736846-267879227.jpg",
    "1742292751972-5919892.jpg",
    "1742292763768-760648252.jpg",
    "1742292777647-282390011.jpg",
    "1742292787191-200632372.jpg",
    "1742292803992-388811771.jpg",
    "1742292819699-640386053.jpg",
    "1742292829151-579681759.jpg",
    "1742292866326-762049518.jpg",
    "1742292877302-235670698.jpg",
    "1742292903962-641372135.jpg",
    "1746612360007-710060961.jpg",
    "1746612510180-634546240.jpg",
    "1746612518224-591242936.jpg",
    "1746612525284-309766377.jpg",
    "1746612533032-854227167.jpg",
    "1746612540855-610484909.jpg",
    "1746612548976-360615927.jpg"
  ];

  // Mock categories for demonstration
  const mockCategories = ['EBC', 'A+ Content', 'Seller Page'];

  const [portfolioItems] = useState(() =>
    portfolioImages.map((img, index) => ({
      id: index + 1,
      title: `Portfolio Item ${index + 1}`,
      description: 'A showcase of our premium work and digital solutions.',
      category: mockCategories[index % mockCategories.length],
      imageUrl: `/portfolio/${img}`,
      tags: [mockCategories[index % mockCategories.length]]
    }))
  );

  // Modal handlers
  const openModal = (project) => {
    setSelectedProject(project);
    document.body.style.overflow = 'hidden';
  };

  const closeModal = () => {
    setSelectedProject(null);
    document.body.style.overflow = 'unset';
  };

  const renderPortfolioItem = (item, index) => (
    <div
      key={`slide-${index}`}
      className="portfolio-slide w-[150px] md:w-[300px] mx-3 cursor-pointer group"
      onClick={() => openModal(item)}
    >
      <motion.div
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        className="relative glass-effect rounded-3xl overflow-hidden h-[500px]"
      >
        {/* Glass Effect Border */}
        <div className="absolute inset-0 ring-1 ring-white/10"></div>

        {/* Image */}
        <div className="h-full overflow-hidden">
          <img
            src={item.imageUrl}
            alt={item.title}
            className="w-full h-full object-cover object-top scale-105 transform group-hover:scale-105 transition-transform duration-500"
            loading="lazy"
            onError={(e) => {
              e.target.onerror = null;
            }}
          />
        </div>

        {/* Content Overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
          <div className="absolute bottom-0 left-0 right-0 p-8">
            <h3 className="text-2xl md:text-3xl font-bold text-white mb-3">
              {item.title}
            </h3>
            <p className="text-base text-gray-300 mb-6">
              {item.description}
            </p>
            <div className="flex flex-wrap gap-3">
              {item.tags?.map((tag, tagIndex) => (
                <span
                  key={tagIndex}
                  className="text-sm px-4 py-2 bg-gray-900/80 backdrop-blur-md rounded-full text-white ring-1 ring-white/10"
                >
                  {tag}
                </span>
              ))}
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );

  // Create duplicated slides for smoother looping
  const allSlides = [...portfolioItems, ...portfolioItems, ...portfolioItems];

  return (
    <>
      <section className="py-20 relative overflow-hidden">
        <div className="container mx-auto px-4 relative">
          {/* Header */}
          <div className="max-w-3xl mx-auto text-center mb-16">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="inline-block"
            >
              <span className="bg-primary/10 backdrop-blur-sm text-primary px-4 py-2 rounded-full text-sm font-medium border border-primary/20">
                Our Portfolio
              </span>
            </motion.div>
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="text-3xl md:text-5xl font-bold text-white mb-6 mt-6"
            >
              Showcasing Excellence on <br />the Amazon Marketplace
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="text-lg text-gray-400 max-w-2xl mx-auto"
            >
              From stunning product photography to engaging Main Images, A+ Content, and Store Designs, explore how we help brands succeed on Amazon.
            </motion.p>
          </div>
        </div>
        {/* Slider */}
        <div className="relative -mx-4">
          <div className="portfolio-track">
            <div className="portfolio-content">
              {allSlides.map((item, index) => item.categoryId !== 5 && renderPortfolioItem(item, index))}
            </div>
          </div>
        </div>
      </section>

      {/* Full-size Image Modal */}
      <AnimatePresence>
        {selectedProject && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 p-4"
            onClick={closeModal}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="relative max-w-7xl w-full max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Close Button */}
              <button
                className="absolute top-4 right-4 z-10 w-10 h-10 rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors flex items-center justify-center"
                onClick={closeModal}
              >
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>

              {/* Modal Content */}
              <div className="bg-black rounded-lg overflow-hidden">
                <img
                  src={selectedProject.imageUrl}
                  alt={selectedProject.title}
                  className="w-full h-auto"
                  onError={(e) => {
                    e.target.onerror = null;
                  }}
                />
                <div className="p-6">
                  <h2 className="text-2xl font-bold text-white mb-4">{selectedProject.title}</h2>
                  <p className="text-gray-300 mb-6">{selectedProject.description}</p>
                  <div className="flex flex-wrap gap-2">
                    {selectedProject.tags?.map((tag, index) => (
                      <span
                        key={index}
                        className="text-sm bg-white/20 text-white/90 px-3 py-1 rounded-full"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default PortfolioSlider;
